import React, { useEffect, useState } from 'react';
import {
  toValidZimbabwePhone,
  ZIMBABWE_PHONE_ERROR,
} from '../../utils/phoneNumber';

interface AddContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contacts: string[]) => void;
}

export const AddContactsModal: React.FC<AddContactsModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [manualNumbers, setManualNumbers] = useState<string[]>([]);
  const [currentNumber, setCurrentNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setManualNumbers([]);
      setCurrentNumber('');
      setPhoneNumberError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAddNumber = () => {
    setPhoneNumberError('');

    if (!currentNumber.trim()) {
      setPhoneNumberError('Please enter a phone number');
      return;
    }

    const cleaned = toValidZimbabwePhone(currentNumber);
    if (!cleaned) {
      setPhoneNumberError(ZIMBABWE_PHONE_ERROR);
      return;
    }

    if (manualNumbers.includes(cleaned)) {
      setPhoneNumberError('This number is already in the list');
      return;
    }

    setManualNumbers([...manualNumbers, cleaned]);
    setCurrentNumber('');
    setPhoneNumberError('');
  };

  const handleRemoveNumber = (number: string) => {
    setManualNumbers(manualNumbers.filter((n) => n !== number));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    if (manualNumbers.length === 0) return;
    onSave(manualNumbers);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Add Contacts</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex space-x-2">
              <input
                type="tel"
                value={currentNumber}
                onChange={(e) => {
                  setCurrentNumber(e.target.value);
                  setPhoneNumberError('');
                }}
                placeholder="Phone number (e.g., 263712345678)"
                className={`flex-1 rounded-md border shadow-sm focus:ring-[#FF5722] focus:border-[#FF5722] ${
                  phoneNumberError ? 'border-red-300' : 'border-gray-300'
                }`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNumber();
                  }
                }}
              />
              <button
                onClick={handleAddNumber}
                className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors"
              >
                Add
              </button>
            </div>
            {phoneNumberError && (
              <p className="mt-1 text-sm text-red-600">{phoneNumberError}</p>
            )}
          </div>
          <div className="border rounded-md p-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">Added Numbers</h3>
              <span className="text-sm text-gray-500">{manualNumbers.length} number(s)</span>
            </div>
            {manualNumbers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No numbers added yet</p>
            ) : (
              <div className="space-y-2">
                {manualNumbers.slice(0, 5).map((number) => (
                  <div key={number} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-600">{number}</span>
                    <button
                      onClick={() => handleRemoveNumber(number)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                {manualNumbers.length > 5 && (
                  <div className="text-center py-2 text-sm text-gray-500 italic">
                    ... and {manualNumbers.length - 5} more number(s)
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={manualNumbers.length === 0}
            className={`px-4 py-2 rounded-md transition-colors ${
              manualNumbers.length > 0
                ? 'bg-[#FF5722] text-white hover:bg-[#F4511E]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            Add Contacts
          </button>
        </div>
      </div>
    </div>
  );
};
